// ═══════════════════════════════════════════════════════════════
// EMC Home — Live Dashboard (V3 redesign)
// Configurable 3-col widget grid · Claude AI brief · Incident table
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback, Component, useRef } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconSparkles, IconX, IconRefresh, IconCircleCheck, IconCalendarEvent,
  IconSettings, IconAlertTriangle, IconChevronDown,
  IconUsers, IconBuilding, IconHeart, IconBriefcase, IconTruck, IconMap,
} from '@tabler/icons-react';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, CircleMarker, Popup, GeoJSON, Marker } from 'react-leaflet';
import L from 'leaflet';
import { useAuthStore } from '../../store/authStore';
import { useRoleStore } from '../../store/roleStore';
import { useBattleRhythmStore } from '../../store/battlerhythmStore';
import { useShelterStore } from '../../store/shelterStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useImpactStore } from '../../store/impactStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useVolunteerStore } from '../../store/volunteerStore';
import { useHandoffStore } from '../../store/handoffStore';
import { useRollCallStore } from '../../store/rollcallStore';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useIntakeStore } from '../../store/intakeStore';
import { useGeoOpsStore } from '../../store/geoopsStore';
import { usePACEStore } from '../../store/paceStore';
import { useBlackboardStore } from '../../store/blackboard-store';
import { useFileLibraryStore } from '../../store/fileLibraryStore';
import { callClaude } from '../../api/worker';
import { queryRecords } from '../../api/client';
import { createRecord, updateRecord } from '../../lib/api-switch';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { WIDGETS, WIDGET_MAP, HOME_WIDGET_KEY, HOME_WIDGET_DEFAULT } from './COP';
import type { Incident } from '../../auth/auth-store';
import { INCIDENT_TYPE_META } from '../incidents/incident-helpers';
import './Dashboard.css';

// ── Error Boundary ────────────────────────────────────────────

interface EBState { error: Error | null; }
class WidgetErrorBoundary extends Component<{ children: ReactNode; title?: string }, EBState> {
  state: EBState = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 8, padding: '24px 16px', color: 'var(--color-error)', flex: 1 }}>
          <IconAlertTriangle size={20} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>Widget error</span>
          <span style={{ fontSize: 12, color: '#939598', textAlign: 'center' }}>{this.state.error.message}</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Helpers ───────────────────────────────────────────────────

function elapsed(startDate?: string): string {
  if (!startDate) return '';
  const ms = Date.now() - new Date(startDate).getTime();
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `T+${d}D ${h}H`;
  return `T+${h}H`;
}

function severityColor(sev?: string): { bg: string; text: string } {
  switch ((sev || '').toLowerCase()) {
    case 'critical': return { bg: 'var(--color-primary-100)',    text: 'var(--color-primary-700)' };
    case 'high':     return { bg: 'var(--cds-red-100)',          text: 'var(--color-error)' };
    case 'medium':   return { bg: 'var(--cds-yellow-100)',       text: 'var(--color-warning)' };
    case 'low':      return { bg: 'var(--cds-green-100)',        text: 'var(--color-success)' };
    default:         return { bg: 'var(--color-neutral-100)',    text: 'var(--color-neutral-600)' };
  }
}

function loadHomeWidgets(): string[] {
  try { const s = localStorage.getItem(HOME_WIDGET_KEY); return s ? JSON.parse(s) : HOME_WIDGET_DEFAULT; }
  catch { return HOME_WIDGET_DEFAULT; }
}

// ── OpenGov AI Informed Update ────────────────────────────────

interface UpdateHighlight { text: string; type: 'critical' | 'warning' | 'info'; }
interface UpdateUpcoming  { text: string; time: string; }
interface UpdateResult {
  summary: string;
  highlights: UpdateHighlight[];
  upcoming: UpdateUpcoming[];
}

// Per-incident cache keyed by setting key (incident id or 'agency')
interface BriefEntry { result: UpdateResult; generatedAt: Date; recordId: number | null; }
const _briefMap = new Map<string, BriefEntry>();
let _briefDismissed = false;

const BRIEF_TTL_MS = 4 * 60 * 60 * 1000;

function briefIsStale(entry: BriefEntry | undefined): boolean {
  return !entry || Date.now() - entry.generatedAt.getTime() > BRIEF_TTL_MS;
}

function timeAgo(d: Date): string {
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return d.toLocaleDateString();
}

const HIGHLIGHT_STYLES: Record<UpdateHighlight['type'], { bg: string; bar: string; color: string }> = {
  critical: { bg: 'var(--background-error-light)',   bar: 'var(--color-error)',         color: 'var(--foreground-error-small)' },
  warning:  { bg: 'var(--background-warning-light)',  bar: 'var(--color-warning)',        color: 'var(--foreground-warning-small)' },
  info:     { bg: 'var(--background-action-light)',   bar: 'var(--color-primary-700)',    color: 'var(--highlights-indigo-dark)' },
};

function elapsedLabel(startDate?: string): string {
  if (!startDate) return '';
  const ms = Date.now() - new Date(startDate).getTime();
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `T+${d}d ${h}h` : `T+${h}h`;
}

function MorningBrief({ incidents, activeIncident }: { incidents: Incident[]; activeIncident: Incident | null }) {
  const briefKey = activeIncident ? `ai-update-${activeIncident.id}` : 'ai-update-agency';

  const [dismissed, setDismissed]     = useState(_briefDismissed);
  const [loading, setLoading]         = useState(false);
  const [result, setResult]           = useState<UpdateResult | null>(_briefMap.get(briefKey)?.result ?? null);
  const [generatedAt, setGeneratedAt] = useState<Date | null>(_briefMap.get(briefKey)?.generatedAt ?? null);
  const [error, setError]             = useState(false);

  // Live store data fed into the prompt
  const shelters    = useShelterStore(s => s.shelters);
  const tickets     = useDebrisOpsStore(s => s.loadTickets);
  const volunteers  = useVolunteerStore(s => s.volunteers);
  const assessments = useImpactStore(s => s.assessments);
  const projects    = usePAGrantsStore(s => s.projects);
  const individuals = useIntakeStore((s: any) => s.individuals as unknown[] ?? []);
  const brEvents    = useBattleRhythmStore(s => s.events);

  const incidentIdStr = activeIncident?.id ? String(activeIncident.id) : '';

  // Keep a ref so generate() always sees latest data without being a dependency
  const dataRef = useRef({ shelters, tickets, volunteers, assessments, projects, individuals, brEvents, incidents, activeIncident });
  dataRef.current = { shelters, tickets, volunteers, assessments, projects, individuals, brEvents, incidents, activeIncident };

  // ── Save to GAB appSettings ───────────────────────────────────
  async function saveBrief(key: string, res: UpdateResult, when: Date) {
    const SF = FIELDS.appSettings;
    const payload = JSON.stringify({ result: res, generatedAt: when.toISOString() });
    const existing = _briefMap.get(key);
    try {
      if (existing?.recordId) {
        await updateRecord(TABLES.appSettings, existing.recordId, [[SF.settingValue, payload]]);
      } else {
        const id = await createRecord(TABLES.appSettings, [
          [SF.settingKey,   key],
          [SF.settingValue, payload],
          [SF.module,       'dashboard'],
          [SF.description,  'OpenGov AI Informed Update — auto-refreshed every 4 hours'],
        ]);
        const entry = _briefMap.get(key);
        if (entry) entry.recordId = id;
      }
    } catch { /* non-critical */ }
  }

  // ── Build context string from live data ───────────────────────
  function buildContext(): string {
    const { shelters, tickets, volunteers, assessments, projects, individuals, brEvents, incidents, activeIncident } = dataRef.current;

    const openShelters = shelters.filter(s => s.status === 'open' || s.status === 'at_capacity');
    const totalOccupancy = openShelters.reduce((n, s) => n + (Number(s.currentPopulation) || 0), 0);
    const totalCapacity  = openShelters.reduce((n, s) => n + (Number(s.capacity) || 0), 0);
    const nearCapacity   = openShelters.filter(s => totalCapacity > 0 &&
      (Number(s.currentPopulation) || 0) / (Number(s.capacity) || 1) >= 0.9).length;
    const atCapacity     = openShelters.filter(s => s.status === 'at_capacity').length;
    const activeVols     = volunteers.filter((v: any) => !v.status || v.status === 'active').length;
    const pendingReview  = assessments.filter((a: any) => a.status === 'review' || a.status === 'pending').length;

    // Upcoming battle rhythm events (next 48h, for this incident)
    const nowMs = Date.now();
    const cutoff = nowMs + 48 * 60 * 60 * 1000;
    const upcomingBR = brEvents
      .filter(e => !incidentIdStr || String(e.incidentId) === incidentIdStr)
      .map(e => {
        const [hh, mm] = (e.scheduledTime || '00:00').split(':').map(Number);
        const t = new Date(); t.setHours(hh || 0, mm || 0, 0, 0);
        return { ...e, _ts: t.getTime() };
      })
      .filter(e => e._ts > nowMs && e._ts < cutoff)
      .sort((a, b) => a._ts - b._ts)
      .slice(0, 5);

    if (activeIncident) {
      return `INCIDENT: ${activeIncident.name}
TYPE: ${activeIncident.incidentType || activeIncident.type || 'Unknown'} | SEVERITY: ${activeIncident.severity || 'Unknown'} | IC: ${activeIncident.commander || 'Unassigned'}
LOCATION: ${activeIncident.location || 'Unknown'}
ELAPSED: ${elapsedLabel(activeIncident.startDate) || 'Unknown'}

LIVE OPERATIONAL DATA:
- Shelters open: ${openShelters.length}${totalCapacity > 0 ? ` | Occupancy: ${totalOccupancy} of ${totalCapacity} (${Math.round(totalOccupancy / totalCapacity * 100)}%)` : ` | Occupancy: ${totalOccupancy}`}${atCapacity > 0 ? ` | ${atCapacity} AT CAPACITY` : ''}${nearCapacity > 0 ? ` | ${nearCapacity} near capacity (≥90%)` : ''}
- Registered individuals: ${individuals.length}
- Debris load tickets: ${tickets.length}
- Volunteers: ${activeVols} active of ${volunteers.length} total
- Impact assessments: ${assessments.length} filed${pendingReview > 0 ? `, ${pendingReview} awaiting review` : ''}
- PA projects: ${projects.length} under management
${upcomingBR.length > 0 ? `\nUPCOMING EVENTS (next 48h):\n${upcomingBR.map(e => `- ${e.scheduledTime} — ${e.title}${e.location ? ` at ${e.location}` : ''}`).join('\n')}` : ''}`;
    } else {
      const active = incidents.filter(i => i.status === 'active' || i.status === 'monitoring');
      return `AGENCY-WIDE PICTURE — ${active.length} active incident${active.length !== 1 ? 's' : ''}
${active.map(i => `- ${i.name}: ${i.incidentType || i.type || 'unknown type'}, ${i.severity || 'unknown severity'}, IC: ${i.commander || 'unassigned'}`).join('\n') || '- No active incidents'}

AGGREGATE OPERATIONAL DATA:
- Shelters open: ${openShelters.length} | Occupancy: ${totalOccupancy}${atCapacity > 0 ? ` (${atCapacity} at capacity)` : ''}
- Registered individuals: ${individuals.length}
- Debris load tickets: ${tickets.length}
- Active volunteers: ${activeVols}
- Impact assessments: ${assessments.length}${pendingReview > 0 ? ` (${pendingReview} pending review)` : ''}
- PA projects: ${projects.length}`;
    }
  }

  // ── Generate + save ───────────────────────────────────────────
  async function generate(key: string) {
    setLoading(true); setError(false);
    try {
      const context = buildContext();

      const system = `You are an AI operational briefing system for a government Emergency Management Operations Center.
You produce concise, factual updates for senior leadership and incident commanders.
Use the specific numbers provided. Be direct — no filler, no preamble.
Never comment on data completeness or missing fields. Only report what is known.`;

      const prompt = `${context}

Produce a concise operational update. Return a single JSON object:
{
  "summary": "2-3 sentences giving the current operational picture with specific numbers from the data",
  "highlights": [
    { "text": "A notable operational fact, pressure point, or developing situation — specific and factual", "type": "critical" | "warning" | "info" },
    ... (2-4 items — use 'critical' for urgent issues, 'warning' for pressure points, 'info' for notable context)
  ],
  "upcoming": [
    { "text": "Event or deadline title", "time": "time string e.g. '14:00' or 'Tomorrow 09:00'" },
    ... (include only if upcoming events were provided; omit array if none)
  ]
}

Return ONLY the JSON. No other text.`;

      const raw = await callClaude([{ role: 'user', content: prompt }], { system, maxTokens: 500 });
      const cleaned = raw.replace(/```json|```/g, '').trim();
      const parsed: UpdateResult = JSON.parse(cleaned);
      if (parsed.summary && Array.isArray(parsed.highlights)) {
        const now = new Date();
        const existing = _briefMap.get(key);
        _briefMap.set(key, { result: parsed, generatedAt: now, recordId: existing?.recordId ?? null });
        setResult(parsed);
        setGeneratedAt(now);
        void saveBrief(key, parsed, now);
      } else setError(true);
    } catch { setError(true); }
    finally { setLoading(false); }
  }

  // ── Load from GAB or generate when key changes ────────────────
  useEffect(() => {
    if (dismissed) return;

    // Reset display to whatever is cached for this key
    const cached = _briefMap.get(briefKey);
    setResult(cached?.result ?? null);
    setGeneratedAt(cached?.generatedAt ?? null);
    setError(false);

    async function init() {
      if (cached && !briefIsStale(cached)) return;

      try {
        const SF = FIELDS.appSettings;
        const res = await queryRecords(TABLES.appSettings, {
          filters: [{ field: SF.settingKey, operator: 'eq', value: briefKey }],
          limit: 1,
        });
        if (res.records.length > 0) {
          const rec = res.records[0] as Record<string, unknown>;
          const recordId = Number(rec.record_id ?? 0) || null;
          const stored = JSON.parse(String(rec[SF.settingValue] ?? '{}'));
          if (stored.result && stored.generatedAt) {
            const savedAt = new Date(stored.generatedAt);
            if (!briefIsStale({ result: stored.result, generatedAt: savedAt, recordId })) {
              _briefMap.set(briefKey, { result: stored.result, generatedAt: savedAt, recordId });
              setResult(stored.result);
              setGeneratedAt(savedAt);
              return;
            }
            // stale but preserve recordId for overwrite
            _briefMap.set(briefKey, { result: stored.result, generatedAt: savedAt, recordId });
          }
        }
      } catch { /* fall through to generate */ }

      void generate(briefKey);
    }

    void init();

    const timer = setInterval(() => {
      if (!dismissed && briefIsStale(_briefMap.get(briefKey))) void generate(briefKey);
    }, 30 * 60 * 1000);
    return () => clearInterval(timer);
  }, [briefKey, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (dismissed) return null;

  return (
    <div className="morning-brief">
      <div className="morning-brief-header">
        <div className="morning-brief-title">
          <IconSparkles size={15} />
          <span>OpenGov Assist</span>
          {generatedAt && !loading && (
            <span className="morning-brief-timestamp">Updated {timeAgo(generatedAt)}</span>
          )}
        </div>
        <div className="morning-brief-actions">
          {!loading && (
            <button className="morning-brief-generate" onClick={() => generate(briefKey)} title="Refresh now">
              <IconRefresh size={13} />
            </button>
          )}
          <button className="morning-brief-dismiss" onClick={() => { _briefDismissed = true; setDismissed(true); }} aria-label="Dismiss">
            <IconX size={14} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="morning-brief-loading">
          <span className="morning-brief-dot" /><span className="morning-brief-dot" /><span className="morning-brief-dot" />
          <span className="morning-brief-loading-text">
            {result ? 'Refreshing update…' : activeIncident ? `Analyzing ${activeIncident.name}…` : 'Generating agency update…'}
          </span>
        </div>
      )}

      {error && !loading && (
        <p className="morning-brief-error">
          Couldn't reach OpenGov AI. <button className="morning-brief-generate" style={{ marginLeft: 8 }} onClick={() => generate(briefKey)}>Retry</button>
        </p>
      )}

      {result && (
        <div className="morning-brief-body" style={loading ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
          <p className="morning-brief-summary" style={{ marginBottom: 16 }}>{result.summary}</p>

          {result.highlights?.length > 0 && (
            <div className="morning-brief-highlights">
              {result.highlights.map((h, i) => {
                const s = HIGHLIGHT_STYLES[h.type] ?? HIGHLIGHT_STYLES.info;
                return (
                  <div key={i} className="morning-brief-highlight" style={{ background: s.bg, borderLeftColor: s.bar }}>
                    <span style={{ color: s.color, fontSize: 12.5 }}>{h.text}</span>
                  </div>
                );
              })}
            </div>
          )}

          {result.upcoming?.length > 0 && (
            <>
              <div className="morning-brief-section-label" style={{ marginTop: 16 }}>Upcoming</div>
              <ul className="morning-brief-upcoming">
                {result.upcoming.map((u, i) => (
                  <li key={i}>
                    <span className="morning-brief-upcoming-time">{u.time}</span>
                    <span>{u.text}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <p className="morning-brief-disclaimer">AI-generated · verify against live data before operational decisions</p>
        </div>
      )}
    </div>
  );
}

// ── Count-up animation hook ───────────────────────────────────

function useCountUp(target: number, duration = 750): number {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    fromRef.current = target;
    if (from === target) return;
    const start = performance.now();
    function tick(now: number) {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }, [target, duration]);
  return display;
}

// ── Alert Rail ────────────────────────────────────────────────

interface AlertItem { id: string; type: 'critical' | 'warning' | 'info'; text: string; }

function AlertRail({ activeIncident }: { activeIncident: Incident | null }) {
  const shelters    = useShelterStore(s => s.shelters);
  const assessments = useImpactStore(s => s.assessments);
  const volunteers  = useVolunteerStore(s => s.volunteers);
  const tickets     = useDebrisOpsStore(s => s.loadTickets);
  const projects    = usePAGrantsStore(s => s.projects);

  const alerts = useMemo<AlertItem[]>(() => {
    const out: AlertItem[] = [];

    if (activeIncident && !activeIncident.commander)
      out.push({ id: 'no-ic',        type: 'critical', text: 'No IC assigned to active incident' });
    if (activeIncident && !activeIncident.severity)
      out.push({ id: 'no-sev',       type: 'warning',  text: 'Incident severity not set' });

    const atCap = shelters.filter(s => s.status === 'at_capacity');
    if (atCap.length > 0)
      out.push({ id: 'shelter-full', type: 'critical', text: `${atCap.length} shelter${atCap.length > 1 ? 's' : ''} at capacity` });

    const nearCap = shelters.filter(s => {
      if (s.status === 'at_capacity') return false;
      const cap = Number(s.capacity) || 0;
      const pop = Number(s.currentPopulation) || 0;
      return cap > 0 && pop / cap >= 0.85;
    });
    if (nearCap.length > 0)
      out.push({ id: 'shelter-near', type: 'warning',  text: `${nearCap.length} shelter${nearCap.length > 1 ? 's' : ''} above 85% capacity` });

    const pending = assessments.filter((a: any) => a.status === 'review' || a.status === 'pending');
    if (pending.length > 0)
      out.push({ id: 'assess-pending', type: 'warning', text: `${pending.length} impact assessment${pending.length > 1 ? 's' : ''} pending review` });

    const openProjects = projects.filter((p: any) => p.status === 'open' || p.status === 'in_progress');
    if (openProjects.length > 5)
      out.push({ id: 'pa-open',      type: 'info',     text: `${openProjects.length} PA projects in progress` });

    const activeVols = volunteers.filter((v: any) => !v.status || v.status === 'active').length;
    if (volunteers.length > 0 && activeVols === 0)
      out.push({ id: 'no-vols',      type: 'warning',  text: 'No active volunteers currently on roster' });

    if (tickets.length === 0 && activeIncident)
      out.push({ id: 'no-tickets',   type: 'info',     text: 'No debris load tickets filed yet' });

    if (out.length === 0 && activeIncident)
      out.push({ id: 'all-clear',    type: 'info',     text: 'All systems nominal — no active alerts' });

    return out;
  }, [shelters, assessments, volunteers, tickets, projects, activeIncident]);

  if (alerts.length === 0) return null;

  const styles = {
    critical: { bg: '#FEF2F2', border: '#FCA5A5', text: '#B91C1C', pulse: true  },
    warning:  { bg: '#FFFBEB', border: '#FCD34D', text: '#92400E', pulse: false },
    info:     { bg: '#EEF2FF', border: '#C7D2FE', text: '#3730A3', pulse: false },
  };

  return (
    <div className="alert-rail">
      {alerts.map(a => {
        const s = styles[a.type];
        return (
          <div key={a.id} className="alert-pill" style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text }}>
            <span className="alert-dot" style={{ background: s.text, animation: s.pulse ? 'status-pulse 1.5s ease infinite' : undefined }} />
            {a.text}
          </div>
        );
      })}
    </div>
  );
}

// ── Situation Charts ──────────────────────────────────────────

function ShelterOccupancyChart() {
  const navigate = useNavigate();
  const shelters = useShelterStore(s => s.shelters);

  const sorted = useMemo(() => {
    return shelters
      .filter(s => s.status === 'open' || s.status === 'at_capacity')
      .map(s => {
        const cap = Number(s.capacity) || 0;
        const pop = Number(s.currentPopulation) || 0;
        return { ...s, cap, pop, pct: cap > 0 ? Math.min(pop / cap, 1) : 0 };
      })
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 7);
  }, [shelters]);

  return (
    <div className="dash-chart-card">
      <div className="dash-chart-header">
        <span className="dash-chart-title">Shelter Occupancy</span>
        <button className="dash-chart-link" onClick={() => navigate('/shelter')}>View all →</button>
      </div>
      {sorted.length === 0 ? (
        <div className="dash-chart-empty">No open shelters</div>
      ) : (
        <div className="dash-chart-bars">
          {sorted.map(s => {
            const color = s.pct > 0.9 ? 'var(--color-error)' : s.pct > 0.7 ? 'var(--data-viz-550)' : 'var(--color-success)';
            return (
              <div key={s.id} className="dash-chart-bar-row">
                <div className="dash-chart-bar-name">{s.name}</div>
                <div className="dash-chart-bar-track">
                  <div className="dash-chart-bar-fill" style={{ width: `${s.pct * 100}%`, background: color }} />
                </div>
                <div className="dash-chart-bar-meta" style={{ color }}>{s.pop}/{s.cap}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PAGrantsStatusChart() {
  const navigate = useNavigate();
  const projects = usePAGrantsStore(s => s.projects);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const p of projects) {
      const st = (p.status || 'draft').toLowerCase();
      c[st] = (c[st] || 0) + 1;
    }
    return c;
  }, [projects]);

  const total = projects.length;

  const STAGE_META: Record<string, { label: string; color: string }> = {
    draft:            { label: 'Draft',            color: '#C68700' },
    submitted:        { label: 'Submitted',        color: '#0095DA' },
    formulation:      { label: 'Formulation',      color: '#4B3FFF' },
    in_review:        { label: 'In Review',        color: '#A627FF' },
    review:           { label: 'In Review',        color: '#A627FF' },
    approved:         { label: 'Approved',         color: '#037730' },
    obligated:        { label: 'Obligated',        color: '#03AC9E' },
    in_progress:      { label: 'In Progress',      color: '#0E6F7F' },
    work_in_progress: { label: 'Work in Progress', color: '#0E6F7F' },
    completed:        { label: 'Completed',        color: '#08A847' },
    closed:           { label: 'Closed',           color: '#546574' },
    appealed:         { label: 'Appealed',         color: '#B12525' },
    closeout:         { label: 'Closeout',         color: '#7402EB' },
    appeal:           { label: 'Appeal',           color: '#B12525' },
    obligation:       { label: 'Obligation',       color: '#03AC9E' },
  };

  const segments = Object.entries(counts)
    .map(([st, val]) => ({
      label: STAGE_META[st]?.label ?? st.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value: val,
      color: STAGE_META[st]?.color ?? '#F86A0B',
    }))
    .filter(s => s.value > 0)
    .sort((a, b) => b.value - a.value);

  const r = 50; const cx = 65; const cy = 65;
  const circ = 2 * Math.PI * r;
  let cumAngle = 0;
  const arcs = segments.map(seg => {
    const dash = (seg.value / total) * circ;
    const arc = { ...seg, strokeDasharray: `${Math.max(dash - 1.5, 0)} ${circ}`, rotation: cumAngle - 90 };
    cumAngle += (seg.value / total) * 360;
    return arc;
  });

  return (
    <div className="dash-chart-card">
      <div className="dash-chart-header">
        <span className="dash-chart-title">PA Grants Pipeline</span>
        <button className="dash-chart-link" onClick={() => navigate('/pagrants')}>View all →</button>
      </div>
      {total === 0 ? (
        <div className="dash-chart-empty">No PA projects on file</div>
      ) : (
        <div className="dash-chart-donut-wrap">
          <svg viewBox="0 0 130 130" style={{ flex: '0 0 auto', width: '100%', maxWidth: 200, height: 'auto' }}>
            {arcs.map((arc, i) => (
              <circle key={i} cx={cx} cy={cy} r={r} fill="none"
                stroke={arc.color} strokeWidth={17}
                strokeDasharray={arc.strokeDasharray}
                transform={`rotate(${arc.rotation}, ${cx}, ${cy})`}
              />
            ))}
            <text x={cx} y={cy - 4} textAnchor="middle"
              style={{ fontSize: 26, fontWeight: 800, fill: '#0f172a', fontFamily: 'system-ui, sans-serif' }}>
              {total}
            </text>
            <text x={cx} y={cy + 13} textAnchor="middle"
              style={{ fontSize: 12, fill: '#939598', fontFamily: 'system-ui, sans-serif' }}>
              projects
            </text>
          </svg>
          <div className="dash-chart-legend">
            {segments.map(s => (
              <div key={s.label} className="dash-chart-legend-row">
                <span className="dash-chart-legend-dot" style={{ background: s.color }} />
                <span className="dash-chart-legend-label">{s.label}</span>
                <span className="dash-chart-legend-val">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AssessmentStatusChart() {
  const navigate    = useNavigate();
  const assessments = useImpactStore(s => s.assessments) as Record<string, unknown>[];

  const counts = useMemo(() => {
    const c = { complete: 0, review: 0, pending: 0, other: 0 };
    for (const a of assessments) {
      const st = String((a as any).status ?? '').toLowerCase();
      if (st === 'complete' || st === 'completed') c.complete++;
      else if (st === 'review') c.review++;
      else if (st === 'pending') c.pending++;
      else c.other++;
    }
    return c;
  }, [assessments]);

  const total = assessments.length;
  const segments = [
    { label: 'Complete',  value: counts.complete, color: 'var(--color-success)' },
    { label: 'In Review', value: counts.review,   color: 'var(--data-viz-550)' },
    { label: 'Pending',   value: counts.pending,  color: 'var(--color-error)' },
    { label: 'Other',     value: counts.other,    color: 'var(--color-neutral-300)' },
  ].filter(s => s.value > 0);

  const r = 50; const cx = 65; const cy = 65;
  const circ = 2 * Math.PI * r;
  let cumAngle = 0;
  const arcs = segments.map(seg => {
    const dash = (seg.value / total) * circ;
    const arc = { ...seg, strokeDasharray: `${Math.max(dash - 1.5, 0)} ${circ}`, rotation: cumAngle - 90 };
    cumAngle += (seg.value / total) * 360;
    return arc;
  });

  return (
    <div className="dash-chart-card">
      <div className="dash-chart-header">
        <span className="dash-chart-title">Impact Assessments</span>
        <button className="dash-chart-link" onClick={() => navigate('/impact')}>View all →</button>
      </div>
      {total === 0 ? (
        <div className="dash-chart-empty">No assessments filed</div>
      ) : (
        <div className="dash-chart-donut-wrap">
          <svg viewBox="0 0 130 130" style={{ flex: '0 0 auto', width: '100%', maxWidth: 200, height: 'auto' }}>
            {arcs.map((arc, i) => (
              <circle key={i} cx={cx} cy={cy} r={r} fill="none"
                stroke={arc.color} strokeWidth={17}
                strokeDasharray={arc.strokeDasharray}
                transform={`rotate(${arc.rotation}, ${cx}, ${cy})`}
              />
            ))}
            <text x={cx} y={cy - 4} textAnchor="middle"
              style={{ fontSize: 26, fontWeight: 800, fill: '#0f172a', fontFamily: 'system-ui, sans-serif' }}>
              {total}
            </text>
            <text x={cx} y={cy + 13} textAnchor="middle"
              style={{ fontSize: 12, fill: '#939598', fontFamily: 'system-ui, sans-serif' }}>
              filed
            </text>
          </svg>
          <div className="dash-chart-legend">
            {segments.map(s => (
              <div key={s.label} className="dash-chart-legend-row">
                <span className="dash-chart-legend-dot" style={{ background: s.color }} />
                <span className="dash-chart-legend-label">{s.label}</span>
                <span className="dash-chart-legend-val">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Incident Command Bar ──────────────────────────────────────

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  const date = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return <div className="home-clock">{time}<span className="home-clock-date"> · {date}</span></div>;
}

function IncidentCommandBar({ incidents, activeIncident, onActivate, onNew }: {
  incidents: Incident[];
  activeIncident: Incident | null;
  onActivate: (inc: Incident) => void;
  onNew: () => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const sev = activeIncident ? severityColor(activeIncident.severity) : null;
  const hasCritical = incidents.some(i => (i.severity || '').toLowerCase() === 'critical');
  const hasHigh     = incidents.some(i => (i.severity || '').toLowerCase() === 'high');
  const sitLabel = hasCritical ? 'EMERGENCY' : hasHigh ? 'ELEVATED' : incidents.length > 0 ? 'ACTIVE OPS' : 'ALL CLEAR';
  const sitPalette: Record<string, { bg: string; text: string }> = {
    EMERGENCY:    { bg: 'var(--cds-red-100)',          text: 'var(--color-error)' },
    ELEVATED:     { bg: 'var(--cds-yellow-100)',        text: 'var(--color-warning)' },
    'ACTIVE OPS': { bg: 'var(--color-primary-100)',     text: 'var(--color-primary-700)' },
    'ALL CLEAR':  { bg: 'var(--cds-green-100)',         text: 'var(--color-success)' },
  };
  const sit = sitPalette[sitLabel];

  return (
    <div className="cmd-bar">
      {/* Left: identity + clock */}
      <div className="cmd-bar-left">
        <div className="cmd-eyebrow">EMC · OPERATIONS CENTER</div>
        <LiveClock />
      </div>

      {/* Center: incident dropdown */}
      <div className="cmd-bar-center" ref={ref}>
        <div className="cmd-eyebrow" style={{ textAlign: 'center', marginBottom: 8 }}>ACTIVE INCIDENT</div>
        <div className="cmd-incident-chip">
          {activeIncident ? (
            <button
              className="cmd-chip-body"
              onClick={() => navigate(`/incidents/${activeIncident.id}`)}
              title="Open incident"
            >
              <span className="cmd-dot" style={{ background: sev?.text ?? '#939598' }} />
              <span className="cmd-incident-name">{activeIncident.name}</span>
              {activeIncident.startDate && (
                <span className="cmd-elapsed">{elapsed(activeIncident.startDate)}</span>
              )}
            </button>
          ) : (
            <span className="cmd-incident-none" style={{ flex: 1 }}>
              {incidents.length > 0 ? 'Select an incident' : 'No active incidents'}
            </span>
          )}
          <button className="cmd-chip-chevron" onClick={() => setOpen(o => !o)} title="Switch incident">
            <IconChevronDown size={14} style={{ color: '#939598', transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }} />
          </button>
        </div>

        {open && (
          <div className="cmd-dropdown">
            {incidents.length === 0 ? (
              <div className="cmd-dropdown-empty">No active incidents on file</div>
            ) : incidents.map(inc => {
              const isAct = activeIncident?.id === inc.id;
              const s = severityColor(inc.severity);
              return (
                <div key={inc.id} className={`cmd-dropdown-row${isAct ? ' cmd-dropdown-row--active' : ''}`}>
                  <div className="cmd-dropdown-info" onClick={() => { setOpen(false); navigate(`/incidents/${inc.id}`); }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.text, flexShrink: 0, display: 'inline-block' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="cmd-dropdown-name">{inc.name}</div>
                      <div className="cmd-dropdown-meta">{[INCIDENT_TYPE_META[(inc.incidentType || inc.type || '').toLowerCase()]?.label || (inc.incidentType || inc.type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || null, inc.location].filter(Boolean).join(' · ') || '—'}</div>
                    </div>
                    {inc.severity && <span className="cmd-sev-chip" style={{ background: s.bg, color: s.text }}>{inc.severity.toUpperCase()}</span>}
                  </div>
                  {isAct
                    ? <span className="cmd-active-badge">● ACTIVE</span>
                    : <button className="cmd-set-active" onClick={() => { onActivate(inc); setOpen(false); }}>Set active</button>
                  }
                </div>
              );
            })}
            <div className="cmd-dropdown-footer">
              <button className="cmd-footer-btn" onClick={() => { setOpen(false); onNew(); }}>+ Declare new incident</button>
              <button className="cmd-footer-link" onClick={() => { setOpen(false); navigate('/incidents'); }}>View all →</button>
            </div>
          </div>
        )}
      </div>

      {/* Right: situation status */}
      <div className="cmd-bar-right">
        <div className="cmd-eyebrow" style={{ textAlign: 'right' }}>SITUATION STATUS</div>
        <div className="cmd-situation" style={{ background: sit.bg, color: sit.text }}>{sitLabel}</div>
        {activeIncident?.commander && <div className="cmd-ic">IC · {activeIncident.commander}</div>}
        {!activeIncident && incidents.length === 0 && (
          <button className="cmd-declare-btn" onClick={onNew}>+ Declare incident</button>
        )}
      </div>
    </div>
  );
}

// ── Live Metric Cards ─────────────────────────────────────────

function MetricCard({ val, label, sub, color, alert, Icon, route }: {
  val: number; label: string; sub: string; color: string;
  alert?: 'critical' | 'warning'; Icon: React.ComponentType<{ size?: number; color?: string }>; route: string;
}) {
  const navigate = useNavigate();
  const display  = useCountUp(val);
  const alertStyle = alert === 'critical'
    ? { border: '1.5px solid #FCA5A5', background: '#FFF5F5' }
    : alert === 'warning'
    ? { border: '1.5px solid #FCD34D', background: '#FFFBEB' }
    : {};
  return (
    <button
      className={`live-metric-card${alert ? ` live-metric-card--${alert}` : ''}`}
      style={alertStyle}
      onClick={() => navigate(route)}
    >
      <Icon size={20} style={{ color, marginBottom: 8, opacity: 0.85 }} />
      <div className="live-metric-val" style={{ color }}>{display}</div>
      <div className="live-metric-label">{label}</div>
      <div className="live-metric-sub">{sub}</div>
    </button>
  );
}

function LiveMetricCards() {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';

  const allShelters    = useShelterStore(s => s.shelters);
  const allTickets     = useDebrisOpsStore(s => s.loadTickets);
  const allVolunteers  = useVolunteerStore(s => s.volunteers);
  const allAssessments = useImpactStore(s => s.assessments);
  const allProjects    = usePAGrantsStore(s => s.projects);
  const individuals    = useIntakeStore((s: any) => s.individuals as unknown[] ?? []);

  const shelters    = incId ? allShelters.filter(s => s.incidentId === incId) : allShelters;
  const tickets     = incId ? allTickets.filter((t: any) => String(t.incidentId ?? '') === incId) : allTickets;
  const volunteers  = incId ? allVolunteers.filter((v: any) => String(v.incidentId ?? '') === incId) : allVolunteers;
  const assessments = incId ? allAssessments.filter((a: any) => String(a.incidentId ?? '') === incId) : allAssessments;
  const projects    = incId ? allProjects.filter((p: any) => String(p.incidentId ?? '') === incId) : allProjects;

  const openShelters = shelters.filter(s => s.status === 'open' || s.status === 'at_capacity').length;
  const shelterPop   = shelters.reduce((n, s) => n + (Number(s.currentPopulation) || 0), 0);
  const shelterCap   = shelters.reduce((n, s) => n + (Number(s.capacity) || 0), 0);
  const activeVols   = volunteers.filter((v: any) => !v.status || v.status === 'active').length;
  const pendingAssess = assessments.filter((a: any) => a.status === 'review' || a.status === 'pending').length;
  const atCapacity   = shelters.some(s => s.status === 'at_capacity');
  const nearCapacity = shelterCap > 0 && shelterPop / shelterCap >= 0.85;

  const metrics: Array<React.ComponentProps<typeof MetricCard>> = [
    { val: individuals.length, label: 'Registered',    sub: 'individuals',                   color: 'var(--color-primary-700)', Icon: IconUsers,         route: '/intake'    },
    { val: openShelters,       label: 'Open Shelters', sub: `${shelterPop} occupants`,        color: 'var(--data-viz-250)',       Icon: IconBuilding,      route: '/shelter',  alert: atCapacity ? 'critical' : nearCapacity ? 'warning' : undefined },
    { val: tickets.length,     label: 'Load Tickets',  sub: 'debris operations',              color: 'var(--data-viz-550)',       Icon: IconTruck,         route: '/debris'    },
    { val: activeVols,         label: 'Volunteers',    sub: 'active & deployed',              color: 'var(--data-viz-200)',       Icon: IconHeart,         route: '/volunteer' },
    { val: assessments.length, label: 'Impact Reports',sub: pendingAssess > 0 ? `${pendingAssess} pending review` : 'filed this incident', color: 'var(--color-error)', Icon: IconAlertTriangle, route: '/impact', alert: pendingAssess > 0 ? 'warning' : undefined },
    { val: projects.length,    label: 'PA Projects',   sub: 'under management',               color: 'var(--data-viz-500)',       Icon: IconBriefcase,     route: '/pagrants'  },
  ];

  return (
    <div className="live-metrics-grid">
      {metrics.map(m => <MetricCard key={m.label} {...m} />)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ── HOME-NATIVE WIDGET CONTENT (no card wrapper — shell provided by grid)
// ══════════════════════════════════════════════════════════════

// ── Readiness Pulse ───────────────────────────────────────────

function ReadinessContent() {
  const navigate = useNavigate();
  const now = Date.now();

  // PACE — tier fill rate across all profiles
  const profiles       = usePACEStore(s => s.profiles);
  const paceLoading    = usePACEStore(s => s.loading);

  // Volunteers — % with certifications on file
  const volunteers     = useVolunteerStore(s => s.volunteers);
  const volLoading     = useVolunteerStore(s => s.loading);

  // Training records — % not expired
  const trainingRecs   = useBlackboardStore(s => s.trainingRecords);
  const bbLoading      = useBlackboardStore(s => s.loading);

  // File library — % of docs not past expiration
  const documents      = useFileLibraryStore(s => s.documents);
  const flLoading      = useFileLibraryStore(s => s.loading);

  useEffect(() => {
    usePACEStore.getState().fetchProfiles();
    useBlackboardStore.getState().loadTrainingRecords();
    useFileLibraryStore.getState().loadDocuments();
  }, []);

  // ── Compute metrics ──────────────────────────────────────────

  // PACE: count filled tier slots across all profiles (4 tiers each)
  const pacePct = (() => {
    if (!profiles.length) return null;
    const total = profiles.length * 4;
    const filled = profiles.reduce((sum, p) => {
      return sum + [p.primary, p.alternate, p.contingency, p.emergency]
        .filter(v => (v || '').trim().length > 0).length;
    }, 0);
    return Math.round((filled / total) * 100);
  })();

  // Volunteers: % with non-empty certifications
  const volPct = (() => {
    if (!volunteers.length) return null;
    const withCerts = volunteers.filter(v => (v.certifications || v.certs || '').trim().length > 0).length;
    return Math.round((withCerts / volunteers.length) * 100);
  })();

  // Training: % of records where expiresDate is blank or in the future
  const trainingPct = (() => {
    if (!trainingRecs.length) return null;
    const current = trainingRecs.filter(r => !r.expiresDate || new Date(r.expiresDate).getTime() > now).length;
    return Math.round((current / trainingRecs.length) * 100);
  })();

  // Plans & SOPs: % of docs with expirationDate blank or in the future
  const docsPct = (() => {
    if (!documents.length) return null;
    const nonExpired = documents.filter(d =>
      !d.expirationDate || new Date(d.expirationDate).getTime() > now
    ).length;
    return Math.round((nonExpired / documents.length) * 100);
  })();

  function barColor(p: number) {
    if (p >= 90) return 'var(--color-success)'; if (p >= 70) return 'var(--data-viz-550)'; return 'var(--color-error)';
  }

  const items = [
    { label: 'PACE tier coverage',              pct: pacePct,    loading: paceLoading, route: '/pace' },
    { label: 'Volunteer credentials on file',   pct: volPct,     loading: volLoading,  route: '/volunteer' },
    { label: 'Training records current',        pct: trainingPct, loading: bbLoading,  route: '/blackboard' },
    { label: 'Plans & SOPs not expired',        pct: docsPct,    loading: flLoading,   route: '/filelibrary' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div className="readiness-list" style={{ flex: 1 }}>
        {items.map(it => (
          <button key={it.label} className="readiness-item" onClick={() => navigate(it.route)}>
            <div className="readiness-item-top">
              <span className="readiness-label">{it.label}</span>
              {it.loading ? (
                <span className="readiness-pct" style={{ color: '#C8C9CA' }}>—</span>
              ) : it.pct === null ? (
                <span className="readiness-pct" style={{ color: '#ADAFB1', fontSize: 12 }}>no data</span>
              ) : (
                <span className="readiness-pct" style={{ color: barColor(it.pct) }}>{it.pct}%</span>
              )}
            </div>
            <div className="readiness-bar-track">
              {it.pct !== null && !it.loading && (
                <div className="readiness-bar-fill" style={{ width: `${it.pct}%`, background: barColor(it.pct) }} />
              )}
            </div>
          </button>
        ))}
      </div>
      <button className="dash-widget-link" style={{ marginTop: 'auto' }} onClick={() => navigate('/pace')}>
        Open readiness report →
      </button>
    </div>
  );
}

// ── Today's Schedule ──────────────────────────────────────────

function TodayScheduleContent() {
  const navigate = useNavigate();
  const activeIncident = useAuthStore(s => s.activeIncident);
  const events = useBattleRhythmStore(s => s.events);
  const loading = useBattleRhythmStore(s => s.loading);
  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const now = Date.now();

  const catColors: Record<string, string> = {
    briefing:  'var(--data-viz-100)',           // blurple
    report:    'var(--data-viz-250)',           // turquoise
    shift:     'var(--data-viz-550)',           // amber
    external:  'var(--color-neutral-500)',
    logistics: 'var(--data-viz-350)',           // blue
    planning:  'var(--data-viz-200)',           // purple
    other:     'var(--color-neutral-500)',
  };
  const statusColors: Record<string, string> = {
    'in-progress': 'var(--color-success)', upcoming: 'var(--color-primary-700)', completed: 'var(--color-neutral-500)', skipped: 'var(--color-neutral-400)',
  };

  const todayEvents = useMemo(() => {
    return events
      .filter(e => String(e.incidentId) === incId)
      .map(e => {
        const [hh, mm] = (e.scheduledTime || '').split(':').map(Number);
        const t = new Date(); t.setHours(hh || 0, mm || 0, 0, 0);
        return { ...e, _ts: t.getTime() };
      })
      .sort((a, b) => a._ts - b._ts);
  }, [events, incId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {!activeIncident ? (
        <div className="widget-empty">
          <IconCalendarEvent size={20} color="#ADAFB1" />
          <span>No active incident selected</span>
        </div>
      ) : loading ? (
        <div className="widget-loading">
          <span className="morning-brief-dot" /><span className="morning-brief-dot" /><span className="morning-brief-dot" />
        </div>
      ) : todayEvents.length === 0 ? (
        <div className="widget-empty">No events scheduled today.</div>
      ) : (
        <div className="br-event-list" style={{ flex: 1, overflow: 'hidden' }}>
          {todayEvents.slice(0, 5).map(e => {
            const isPast = e._ts < now && e.status !== 'in-progress';
            return (
              <div key={e.id} className="br-event-row"
                style={{ borderLeftColor: catColors[e.category] || '#DDDEDE', opacity: isPast && e.status === 'completed' ? 0.5 : 1 }}>
                <span className="br-event-time">{e.scheduledTime}</span>
                <div className="br-event-body">
                  <span className="br-event-title">{e.title}</span>
                  {e.location && <span className="br-event-location">{e.location}</span>}
                </div>
                <span className="br-event-status" style={{ color: statusColors[e.status] || '#939598' }}>{e.status}</span>
              </div>
            );
          })}
          {todayEvents.length > 5 && (
            <div style={{ fontSize: 12, color: '#ADAFB1', padding: '4px 0' }}>+{todayEvents.length - 5} more</div>
          )}
        </div>
      )}
      <button className="dash-widget-link" style={{ marginTop: 'auto' }} onClick={() => navigate('/battlerhythm')}>
        Open battle rhythm →
      </button>
    </div>
  );
}

// ── Pending Work ──────────────────────────────────────────────

interface PendingCounts { assessments: number; workflows: number; paProjects: number; }

function PendingWorkContent() {
  const navigate = useNavigate();
  const [counts, setCounts]   = useState<PendingCounts | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [assessRes, workflowRes, paRes] = await Promise.all([
          queryRecords(TABLES.impactAssessments, {
            filters: [{ field: FIELDS.impactAssessments.status, operator: 'eq', value: 'review' }], limit: 1,
          }).catch(() => ({ total: 0, records: [] })),
          queryRecords(TABLES.workflowInstances, { limit: 200 }).catch(() => ({ total: 0, records: [] })),
          queryRecords(TABLES.paProjects,         { limit: 200 }).catch(() => ({ total: 0, records: [] })),
        ]);
        if (cancelled) return;
        const assessments = assessRes.total ?? (assessRes.records?.length ?? 0);
        const workflowCount = (workflowRes.records as unknown as Record<string, unknown>[])
          .filter(r => { const s = String(r[FIELDS.workflowInstances.status] ?? ''); return s === 'pending' || s === 'in_progress'; }).length;
        const paCount = (paRes.records as unknown as Record<string, unknown>[])
          .filter(r => { const s = String(r[FIELDS.paProjects.status] ?? ''); return s === 'open' || s === 'in_progress'; }).length;
        setCounts({ assessments, workflows: workflowCount, paProjects: paCount });
      } catch { setCounts({ assessments: 0, workflows: 0, paProjects: 0 }); }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const allClear = counts && counts.assessments === 0 && counts.workflows === 0 && counts.paProjects === 0;

  if (loading) return (
    <div className="widget-loading" style={{ flex: 1 }}>
      <span className="morning-brief-dot" /><span className="morning-brief-dot" /><span className="morning-brief-dot" />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {allClear ? (
        <div className="pending-all-clear" style={{ flex: 1 }}>
          <IconCircleCheck size={24} color="var(--color-success)" />
          <span>All caught up</span>
        </div>
      ) : (
        <div className="pending-stats" style={{ flex: 1 }}>
          {[
            { num: counts?.assessments ?? 0, label: 'Impact assessments in review', route: '/impact', color: 'var(--color-warning)' },
            { num: counts?.workflows ?? 0,   label: 'Workflow instances in progress', route: '/workflows', color: 'var(--color-warning)' },
            { num: counts?.paProjects ?? 0,  label: 'PA projects open or in progress', route: '/pagrants', color: 'var(--color-error)' },
          ].map(r => (
            <div key={r.label} className="pending-stat-row">
              <div className="pending-stat-left">
                <span className="pending-stat-num" style={{ color: r.num > 0 ? r.color : 'var(--color-neutral-400)' }}>{r.num}</span>
                <span className="pending-stat-label">{r.label}</span>
              </div>
              <button className="pending-view-link" onClick={() => navigate(r.route)}>View →</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Native widget registry (content components + metadata) ────

export const NATIVE_WIDGETS = [
  { id: 'readiness', title: 'Readiness Pulse',   color: 'var(--color-success)', Comp: ReadinessContent },
  { id: 'schedule',  title: "Today's Schedule",   color: 'var(--data-viz-250)',  Comp: TodayScheduleContent },
  { id: 'pending',   title: 'Pending Work',       color: 'var(--data-viz-550)', Comp: PendingWorkContent },
] as const;

// ── Dashboard Map Panel ───────────────────────────────────────

function DashMapPanel() {
  const navigate      = useNavigate();
  const activeIncident = useAuthStore(s => s.activeIncident);
  const shelters      = useShelterStore(s => s.shelters);
  const zones         = useGeoOpsStore(s => s.zones);
  const individuals   = useIntakeStore((s: any) => s.individuals as unknown[] ?? []);

  const incId = activeIncident?.id ? String(activeIncident.id) : '';
  const lat   = activeIncident?.latitude  ? parseFloat(activeIncident.latitude)  : null;
  const lon   = activeIncident?.longitude ? parseFloat(activeIncident.longitude) : null;

  const iShelters   = (incId ? shelters.filter(s => s.incidentId === incId) : shelters);
  const openShelters = iShelters.filter(s => s.status === 'open' || s.status === 'at_capacity');
  const iZones      = (incId ? zones.filter(z => String(z.incidentId) === incId) : zones).filter(z => z.status === 'active');
  const totalOcc    = openShelters.reduce((n, s) => n + (Number(s.currentPopulation) || 0), 0);

  const center: [number, number] = lat && lon ? [lat, lon] : [38.5, -98];

  const incidentIcon = useMemo(() => L.divIcon({
    className: '',
    html: '<div class="dash-map-epicenter"><div class="dash-map-dot"></div></div>',
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -22],
  }), []);

  if (!lat || !lon) {
    return (
      <div className="dash-map-panel dash-map-panel--empty">
        <IconMap size={28} color="#ADAFB1" />
        <span>Add lat/lon to the active incident to show the situation map</span>
      </div>
    );
  }

  return (
    <div className="dash-map-panel">
      <MapContainer
        center={center} zoom={11}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        zoomControl={true}
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {/* Incident epicenter — pulsing divIcon */}
        <Marker position={center} icon={incidentIcon}>
          <Popup><strong>{activeIncident?.name}</strong><br />Incident epicenter</Popup>
        </Marker>

        {/* Shelters */}
        {iShelters.filter(s => s.latitude && s.longitude).map(s => {
          const pct   = Number(s.capacity) > 0 ? (Number(s.currentPopulation) || 0) / Number(s.capacity) : 0;
          const color = pct > 0.9 ? 'var(--color-error)' : pct > 0.7 ? 'var(--data-viz-550)' : 'var(--color-success)';
          return (
            <CircleMarker
              key={s.id}
              center={[parseFloat(s.latitude), parseFloat(s.longitude)]}
              radius={7}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.8, weight: 2 }}
            >
              <Popup><strong>{s.name}</strong><br />{s.currentPopulation}/{s.capacity} · {s.status}</Popup>
            </CircleMarker>
          );
        })}

        {/* GeoOps zones */}
        {iZones.filter(z => z.geometry).map(z => (
          <GeoJSON
            key={z.id}
            data={z.geometry as any}
            style={{ color: z.color || '#d97706', fillColor: z.color || '#d97706', fillOpacity: z.opacity ?? 0.15, weight: 2 }}
          />
        ))}
      </MapContainer>

      {/* ── HUD overlay — top-left glass card ── */}
      <div className="dash-map-hud">
        <div className="dash-map-hud-row">
          <span className="dash-map-hud-dot" style={{ background: 'var(--color-primary-700)' }} />
          <span>Incident epicenter</span>
        </div>
        <div className="dash-map-hud-row">
          <span className="dash-map-hud-dot" style={{ background: 'var(--color-success)' }} />
          <span>{openShelters.length} shelter{openShelters.length !== 1 ? 's' : ''}</span>
          {totalOcc > 0 && <span className="dash-map-hud-muted">· {totalOcc} occ.</span>}
        </div>
        <div className="dash-map-hud-row">
          <span className="dash-map-hud-dot" style={{ background: 'var(--color-primary-700)', opacity: 0.5 }} />
          <span>{individuals.length} registered</span>
        </div>
        {iZones.length > 0 && (
          <div className="dash-map-hud-row">
            <span className="dash-map-hud-dot" style={{ background: 'var(--data-viz-550)' }} />
            <span>{iZones.length} active zone{iZones.length !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* ── Open in GeoOps — bottom-right ── */}
      <button className="dash-map-geoops-btn" onClick={() => navigate('/geoops')}>
        Open in GeoOps →
      </button>
    </div>
  );
}

// ── Agency-Wide Stat Strip ────────────────────────────────────

function StatStrip() {
  const navigate       = useNavigate();
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incId          = activeIncident?.id ? String(activeIncident.id) : '';

  const allShelters    = useShelterStore(s => s.shelters);
  const allTickets     = useDebrisOpsStore(s => s.loadTickets);
  const allVolunteers  = useVolunteerStore(s => s.volunteers);
  const allAssessments = useImpactStore(s => s.assessments);
  const allProjects    = usePAGrantsStore(s => s.projects);
  const individuals    = useIntakeStore((s: any) => s.individuals as unknown[] ?? []);

  const shelters    = incId ? allShelters.filter(s => s.incidentId === incId) : allShelters;
  const tickets     = incId ? allTickets.filter((t: any) => String(t.incidentId ?? '') === incId) : allTickets;
  const volunteers  = incId ? allVolunteers.filter((v: any) => String(v.incidentId ?? '') === incId) : allVolunteers;
  const assessments = incId ? allAssessments.filter((a: any) => String(a.incidentId ?? '') === incId) : allAssessments;
  const projects    = incId ? allProjects.filter((p: any) => String(p.incidentId ?? '') === incId) : allProjects;

  const openShelters  = shelters.filter(s => s.status === 'open' || s.status === 'at_capacity').length;
  const shelterPop    = shelters.reduce((n, s) => n + (Number(s.currentPopulation) || 0), 0);
  const activeVols    = volunteers.filter((v: any) => !v.status || v.status === 'active').length;
  const pendingAssess = assessments.filter((a: any) => a.status === 'review' || a.status === 'pending').length;
  const atCap         = shelters.some(s => s.status === 'at_capacity');
  const hasPending    = pendingAssess > 0;

  const stats = [
    { val: individuals.length, label: 'Registered',     sub: 'individuals',        color: 'var(--color-primary-700)', alert: false,     route: '/intake'    },
    { val: openShelters,       label: 'Open Shelters',  sub: `${shelterPop} occ.`, color: 'var(--data-viz-250)',       alert: atCap,     route: '/shelter'   },
    { val: activeVols,         label: 'Volunteers',     sub: 'active',             color: 'var(--data-viz-200)',       alert: false,     route: '/volunteer' },
    { val: assessments.length, label: 'Assessments',    sub: hasPending ? `${pendingAssess} pending` : 'filed', color: 'var(--data-viz-550)', alert: hasPending, route: '/impact'    },
    { val: tickets.length,     label: 'Debris Loads',   sub: 'tickets',            color: 'var(--data-viz-650)',       alert: false,     route: '/debris'    },
    { val: projects.length,    label: 'PA Projects',    sub: 'under management',   color: 'var(--data-viz-500)',       alert: false,     route: '/pagrants'  },
  ];

  return (
    <div className="stat-strip">
      {stats.map((s, i) => (
        <button key={s.label} className={`stat-strip-item${s.alert ? ' stat-strip-item--alert' : ''}`} onClick={() => navigate(s.route)}>
          <span className="stat-strip-val" style={{ color: s.alert ? '#D33423' : s.color }}>{s.val}</span>
          <span className="stat-strip-label">{s.label}</span>
          <span className="stat-strip-sub">{s.sub}</span>
          {i < stats.length - 1 && <span className="stat-strip-divider" />}
        </button>
      ))}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────

function timeOfDay(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

// ── Home Header ───────────────────────────────────────────────

function HomeHeader({ incidents, onNew }: { incidents: Incident[]; onNew: () => void }) {
  const user = useAuthStore(s => s.user);
  const hasCritical = incidents.some(i => (i.severity || '').toLowerCase() === 'critical');
  const hasHigh     = incidents.some(i => (i.severity || '').toLowerCase() === 'high');
  const sitLabel = hasCritical ? 'EMERGENCY' : hasHigh ? 'ELEVATED' : incidents.length > 0 ? 'ACTIVE OPS' : 'ALL CLEAR';
  const sitPalette: Record<string, { bg: string; text: string }> = {
    EMERGENCY:    { bg: 'var(--cds-red-100)',          text: 'var(--color-error)' },
    ELEVATED:     { bg: 'var(--cds-yellow-100)',        text: 'var(--color-warning)' },
    'ACTIVE OPS': { bg: 'var(--color-primary-100)',     text: 'var(--color-primary-700)' },
    'ALL CLEAR':  { bg: 'var(--cds-green-100)',         text: 'var(--color-success)' },
  };
  const sit = sitPalette[sitLabel];
  const firstName = (user as any)?.firstName || '';

  return (
    <div className="home-header">
      {/* Left — identity */}
      <div className="home-header-left">
        <div className="home-header-eyebrow">Emergency Management Console</div>
        <div className="home-header-greeting">
          {firstName ? `Good ${timeOfDay()}, ${firstName}` : `Good ${timeOfDay()}`}
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {incidents.length > 0 ? (
            <span style={{ fontSize: 12, color: '#7B7D7F', fontWeight: 500 }}>
              {incidents.length} active incident{incidents.length !== 1 ? 's' : ''} · {incidents.filter(i => i.status === 'active').length} in response
            </span>
          ) : (
            <span style={{ fontSize: 12, color: '#939598', fontWeight: 500 }}>No active incidents on file</span>
          )}
        </div>
      </div>

      {/* Center — clock */}
      <LiveClock />

      {/* Right — status + action */}
      <div className="home-header-right">
        <span className="home-header-sit" style={{ background: sit.bg, color: sit.text }}>{sitLabel}</span>
        <button className="dash-new-btn" onClick={onNew}>+ Declare Incident</button>
      </div>
    </div>
  );
}

// ── Active Incidents Summary ──────────────────────────────────

function ActiveIncidentsSummary({ incidents, activeIncident, onActivate, onNew }: {
  incidents: Incident[];
  activeIncident: Incident | null;
  onActivate: (inc: Incident) => void;
  onNew: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div className="incident-table-card">
      <div className="dash-incidents-card-header">
        <div>
          <h2 className="dash-section-title">
            Active Incidents
            {incidents.length > 0 && <span className="dash-incident-count">{incidents.length}</span>}
          </h2>
          <p className="dash-section-sub" style={{ fontSize: 12 }}>Declared incidents currently requiring response</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="cmd-footer-link" style={{ fontSize: 14 }} onClick={() => navigate('/incidents')}>View all →</button>
          <button className="dash-new-btn" onClick={onNew}>+ Declare</button>
        </div>
      </div>

      {incidents.length === 0 ? (
        <div className="no-incidents" style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
          <IconCircleCheck size={28} color="var(--color-success)" />
          <h3>No active incidents</h3>
          <p>All declared incidents have been closed or resolved. The agency is operating at normal posture.</p>
          <button className="dash-new-btn" onClick={onNew}>+ Declare Incident</button>
        </div>
      ) : (
        <table className="incident-table">
          <thead>
            <tr>
              <th className="incident-th" style={{ textAlign: 'left' }}>Incident</th>
              <th className="incident-th">Type</th>
              <th className="incident-th" style={{ textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {incidents.map(inc => {
              const isAct = activeIncident?.id === inc.id;
              const sev = severityColor(inc.severity);
              const dotClass = inc.status === 'active' ? 'status-dot status-dot--active'
                : inc.status === 'monitoring' ? 'status-dot status-dot--monitoring'
                : 'status-dot';
              return (
                <tr key={inc.id} className={`incident-tr${isAct ? ' incident-tr--active-row' : ''}`} onClick={() => navigate(`/incidents/${inc.id}`)}>
                  <td className="incident-td">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className={dotClass} />
                      <span className="incident-name">{inc.name}</span>
                      {isAct && <span className="active-badge">ACTIVE</span>}
                    </div>
                  </td>
                  <td className="incident-td incident-td--muted">{(() => { const t = ((inc as any).incidentType || (inc as any).type || '').toLowerCase(); return INCIDENT_TYPE_META[t]?.label || t.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || '—'; })()}</td>
                  <td className="incident-td" style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                      {!isAct && (
                        <button className="set-active-btn" onClick={() => onActivate(inc)}>Set active</button>
                      )}
                      <button className="set-active-btn" style={{ color: 'var(--color-primary-700)', borderColor: 'var(--color-primary-200)' }}
                        onClick={() => navigate(`/incidents/${inc.id}`)}>
                        Open →
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const { incidents, activeIncident, setActiveIncident, loadIncidents } = useAuthStore();
  const { myRole } = useRoleStore();
  const loadActivations = useBattleRhythmStore(s => s.loadActivations);
  const loadEvents      = useBattleRhythmStore(s => s.loadEvents);

  const [homeWidgetIds, setHomeWidgetIds] = useState<string[]>(loadHomeWidgets);

  // Re-sync from localStorage when window regains focus (user returning from Settings)
  useEffect(() => {
    function onFocus() {
      try { const s = localStorage.getItem(HOME_WIDGET_KEY); if (s) setHomeWidgetIds(JSON.parse(s)); }
      catch {}
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Redirect biz_owner
  useEffect(() => {
    if (myRole?.roleSlug === 'biz_owner') navigate('/mainstreet', { replace: true });
  }, [myRole, navigate]);

  useEffect(() => { loadIncidents(); }, []);

  // Always load data for the live metric cards
  const incidentId = activeIncident?.id?.toString() ?? '';
  useEffect(() => {
    useShelterStore.getState().loadShelters();
    useDebrisOpsStore.getState().loadLoadTickets();
    useVolunteerStore.getState().loadVolunteers();
    useImpactStore.getState().loadAssessments();
    usePAGrantsStore.getState().fetchProjects();
    if (incidentId) useIntakeStore.getState().loadIndividuals(incidentId);
  }, [incidentId]);

  // Load module data for any active COP widgets
  const loadWidgetData = useCallback(() => {
    const hasCopWidgets = homeWidgetIds.some(id => !['readiness', 'schedule', 'pending'].includes(id));
    if (!hasCopWidgets && !homeWidgetIds.includes('schedule')) return;
    useShelterStore.getState().loadShelters();
    usePAGrantsStore.getState().fetchProjects();
    usePAGrantsStore.getState().fetchReimbursements();
    useImpactStore.getState().loadAssessments();
    useDebrisOpsStore.getState().loadLoadTickets();
    useVolunteerStore.getState().loadVolunteers();
    useVolunteerStore.getState().loadAssignments();
    useHandoffStore.getState().loadNotes();
    useRollCallStore.getState().loadAll();
    useMainStreetStore.getState().loadOrganizations();
    useGeoOpsStore.getState().fetchZones();
    if (activeIncident?.id) {
      const incId = String(activeIncident.id);
      useIntakeStore.getState().loadIndividuals(incId);
      loadActivations(incId);
      loadEvents(incId);
    }
  }, [activeIncident?.id, homeWidgetIds, loadActivations, loadEvents]);

  useEffect(() => { loadWidgetData(); }, [loadWidgetData]);

  const activeIncidents = incidents.filter(i => i.status === 'active' || i.status === 'monitoring');

  return (
    <div className="emc-dashboard">

      <HomeHeader incidents={activeIncidents} onNew={() => navigate('/incidents/new')} />

      {/* ── Primary two-column layout ── */}
      <div className="dash-primary">

        {/* Left: incidents → metric cards */}
        <div className="dash-primary-left">
          <ActiveIncidentsSummary
            incidents={activeIncidents}
            activeIncident={activeIncident}
            onActivate={setActiveIncident}
            onNew={() => navigate('/incidents/new')}
          />
          <div className="dash-left-spacer" />
          <LiveMetricCards />
        </div>

        {/* Right: stacked charts */}
        <div className="dash-primary-right">
          <AssessmentStatusChart />
          <PAGrantsStatusChart />
        </div>

      </div>

      {/* ── Shelter occupancy — full width ── */}
      <div className="dash-shelter-row">
        <ShelterOccupancyChart />
      </div>

      {/* ── Configurable widget grid ── */}
      <div>
        <div className="dash-section-header">
          <div>
            <h2 className="dash-section-title">Operational Widgets</h2>
            <p className="dash-section-sub">
              Live module data ·{' '}
              <button className="home-widgets-settings-link" onClick={() => navigate('/settings/dashboard')}>
                <IconSettings size={12} /> Customize
              </button>
            </p>
          </div>
        </div>
        <div className="dash-widgets-grid">
          {homeWidgetIds.map(id => {
            const native = NATIVE_WIDGETS.find(w => w.id === id);
            const copDef = WIDGETS.find(w => w.id === id);
            const CopComp = WIDGET_MAP[id];

            if (native) {
              return (
                <div key={id} className="dash-widget" style={{ borderTop: `3px solid ${native.color}` }}>
                  <div className="dash-widget-header">
                    <span className="dash-widget-title">{native.title}</span>
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <WidgetErrorBoundary title={native.title}>
                      <native.Comp />
                    </WidgetErrorBoundary>
                  </div>
                </div>
              );
            }

            if (copDef && CopComp) {
              const cardColor = copDef.color.startsWith('var(') ? '#4B3FFF' : copDef.color;
              return (
                <div key={id} className="dash-widget" style={{ borderTop: `3px solid ${cardColor}` }}>
                  <div className="dash-widget-header">
                    <span className="dash-widget-dot" style={{ background: cardColor }} />
                    <span className="dash-widget-title">{copDef.title}</span>
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <WidgetErrorBoundary title={copDef.title}>
                      <CopComp />
                    </WidgetErrorBoundary>
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>

    </div>
  );
}
